import {Icon, TextInput} from "@renta-apps/athenaeum-react-components";
import Localizer from "../../localization/Localizer";
import React, {FocusEvent, forwardRef, useEffect, useState} from "react";
import {SearchedDeviceResponse} from "@/models/server/responses/SearchedDeviceResponse";
import HttpClient from "@/common/HttpClient";
import EndpointPaths from "@/common/EndpointPaths";
import {SearchedDevice} from "@/models/SearchedDevice";
import styles from "./DeviceSearch.module.scss";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import Show from "../Show/Show";
import RentaToolsController from "@/pages/RentaToolsController";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import Debouncer from "@/helpers/Debouncer";
import ToolsUtility from "@/helpers/ToolsUtility";
import UnleashHelper from "@/helpers/UnleashHelper";

const DeviceSearch = forwardRef<TextInput>((props, ref) => {
    const [searchTermValue, setSearchTerm] = useState<string>("");
    const [searchedDevices, setSearchedDevices] = useState<SearchedDevice[]>([]);
    const [showItems, setShowItems] = useState<boolean>(false);
    const [hasCandidates, setHasCandidates] = useState<boolean>(false);
    const [extendedSearchEnabled, setExtendedSearchEnabled] = useState<boolean>(false);
    const [useResponsiveSearch, setUseResponsiveSearch] = useState<boolean>(false);

    const debouncer = new Debouncer();

    useEffect(() => {
        const extendedSearchEnabled = localStorage.getItem(RentaToolsConstants.extendedSearchEnabled) === "true";
        setExtendedSearchEnabled(extendedSearchEnabled);
    }, [localStorage.getItem(RentaToolsConstants.extendedSearchEnabled)]);

    useEffect(() => {
        setUseResponsiveSearch(UnleashHelper.isEnabled(RentaToolsConstants.featureExtendedSearchCapabilitiesEnabled));
    }, [UnleashHelper.isEnabled(RentaToolsConstants.featureExtendedSearchCapabilitiesEnabled)]);

    const search = async (searchTerm: string, extendedSearchEnabled : boolean) : Promise<void> => {
        if(extendedSearchEnabled) {
            if (searchTerm.length < 3) {
                setSearchedDevices([]);
                setShowItems(false);
                return;
            }

            const response: SearchedDeviceResponse = await HttpClient.postAsync(EndpointPaths.DevicePaths.SearchDevice, searchTerm);

            setHasCandidates(response.hasCandidates);

            if (response.hasCandidates) {
                setShowItems(true);
                setSearchedDevices(response.devices);
            } else {
                setShowItems(false);
                setSearchedDevices([]);
            }
        }
    }

    const saveSearchTerm = (value: string) => {
        setSearchTerm(value);
    }

    const debouncedSearch = (value: string, extendedSearchEnabled : boolean) => {
        setSearchTerm(value);
        debouncer.debounce(() => search(value, extendedSearchEnabled), 500);
    };

    const onBlur = (e : FocusEvent<HTMLDivElement>) : void => {
        e.stopPropagation();

        setTimeout(() => {
            setShowItems(false);
        }, 200);
    }

    const onFocus = () => {
        const shouldShow = hasCandidates && extendedSearchEnabled;
        setShowItems(shouldShow);
    }

    const onClick = (deviceId : string) => {
        return RentaToolsController.loadDeviceAsync(deviceId);
    }

    const onSearchIconClick = () => {
        if (!useResponsiveSearch) {
            if (ToolsUtility.isNullOrEmpty(searchTermValue)) {
                return;
            }

            return RentaToolsController.searchDeviceAsync(searchTermValue);
        }

        const searchEnabled = !extendedSearchEnabled;
        setExtendedSearchEnabled(searchEnabled);
        localStorage.setItem(RentaToolsConstants.extendedSearchEnabled, (searchEnabled).toString());
    }

    return (
        <div className={styles.deviceSearchContainer} onFocus={() => onFocus()} onBlur={(e :  FocusEvent<HTMLDivElement>) => onBlur(e)}>
            <div className={styles.searchDeviceInputContainer}>
                <div className={styles.searchDeviceInput}>

                    <Show.When isTrue={useResponsiveSearch} >
                        <TextInput required
                                   ref={ref}
                                   id="deviceId"
                                   value={searchTermValue}
                                   onValidationError={() => Localizer.dashboardPageDeviceIdRequired}
                                   placeholder={Localizer.dashboardInsertRentaID}
                                   onChange={async (e) => debouncedSearch(e.value, extendedSearchEnabled)}
                                   className={styles.searchInput}
                        />
                    </Show.When>

                    <Show.When isTrue={!useResponsiveSearch} >
                        <TextInput required
                                   ref={ref}
                                   id="deviceId"
                                   value={searchTermValue}
                                   onValidationError={() => Localizer.dashboardPageDeviceIdRequired}
                                   placeholder={Localizer.dashboardInsertRentaID}
                                   onChange={async (e) => saveSearchTerm(e.value)}
                                   className={styles.searchInput}
                        />
                    </Show.When>

                </div>
                <div className={Utility.css(extendedSearchEnabled && styles.searchIconEnabled, styles.searchIcon)} onClick={() => onSearchIconClick()}>
                    <Icon name="fas fa-search" />
                </div>
            </div>

            <Show.When isTrue={showItems}>
                <div className={styles.searchDeviceResults}>
                    {searchedDevices.map((device, index) => (
                        <div onClick={() => onClick(device.deviceId)} key={index} className={Utility.css(styles.row, index !== searchedDevices.length - 1 && styles.rowBorderBottom)}>
                            <div className={styles.firstRow}>
                                <div>{device.deviceExternalId}</div>
                                <div>{device.deviceType}</div>
                                <div className={styles.rightCell}>{device.depo}</div>
                            </div>

                            <div className={styles.secondRow}>
                                <div>{device.productGroup}</div>
                                <div>{device.serialNumber}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Show.When>
        </div>
    )
})

export default DeviceSearch;